import { useRef, useEffect, useCallback } from 'react';

const TAP_THRESHOLD = 10;

/**
 * Distinguishes taps from scrolls on touch devices.
 *
 * Returns pointer event handlers (`onPointerDown` / `onPointerUp`) and
 * a helper that checks whether the last touch interaction was a tap
 * (finger moved less than 10px) or a scroll.
 */
export function useTapDetection() {
    const startPos = useRef<{ x: number; y: number } | null>(null);

    const onPointerDown = (e: React.PointerEvent) => {
        if (e.pointerType === 'touch') {
            startPos.current = { x: e.clientX, y: e.clientY };
        }
    };

    const wasTap = (e: React.PointerEvent): boolean => {
        if (e.pointerType !== 'touch' || !startPos.current) return false;
        const dx = Math.abs(e.clientX - startPos.current.x);
        const dy = Math.abs(e.clientY - startPos.current.y);
        startPos.current = null;
        return dx < TAP_THRESHOLD && dy < TAP_THRESHOLD;
    };

    const cancelTap = () => { startPos.current = null; };

    return { onPointerDown, wasTap, cancelTap };
}

/**
 * Calls `onDismiss` when the user taps outside `triggerElement` on touch.
 * Ignores scroll gestures (finger moved more than 10px).
 */
export function useDismissOnOutsideTap(
    active: boolean,
    triggerElement: HTMLElement | null | undefined,
    onDismiss: () => void,
) {
    useEffect(() => {
        if (!active) return;

        let startPos: { x: number; y: number } | null = null;

        const onStart = (e: TouchEvent) => {
            startPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        };
        const onEnd = (e: TouchEvent) => {
            if (!startPos || !e.changedTouches[0]) return;
            const dx = Math.abs(e.changedTouches[0].clientX - startPos.x);
            const dy = Math.abs(e.changedTouches[0].clientY - startPos.y);
            if (dx < TAP_THRESHOLD && dy < TAP_THRESHOLD && triggerElement && !triggerElement.contains(e.target as Node)) {
                onDismiss();
            }
            startPos = null;
        };

        document.addEventListener('touchstart', onStart, { passive: true });
        document.addEventListener('touchend', onEnd, { passive: true });
        return () => {
            document.removeEventListener('touchstart', onStart);
            document.removeEventListener('touchend', onEnd);
        };
    }, [active, triggerElement, onDismiss]);
}

/**
 * Calls `onDismiss` when the user presses the mouse outside `triggerElement`.
 * Uses `mousedown` so the popup closes on press-down without racing the open
 * handler. Touch outside-dismiss is handled separately by `useDismissOnOutsideTap`.
 */
export function useDismissOnOutsideClick(
    active: boolean,
    triggerElement: HTMLElement | null | undefined,
    onDismiss: () => void,
) {
    useEffect(() => {
        if (!active) return;

        const onMouseDown = (e: MouseEvent) => {
            if (triggerElement && !triggerElement.contains(e.target as Node)) {
                onDismiss();
            }
        };

        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [active, triggerElement, onDismiss]);
}

/**
 * Calls `onDismiss` when the scroll container scrolls.
 *
 * By default it listens on the global `[data-section-container]`. Pass a
 * `getScroller` resolver to scope the listener to a specific element (e.g. the
 * panel's own section scroller) so scrolling in other sections is ignored.
 */
export function useDismissOnScroll(
    active: boolean,
    onDismiss: () => void,
    getScroller?: () => Element | null,
) {
    const stableOnDismiss = useCallback(onDismiss, [onDismiss]);

    useEffect(() => {
        if (!active) return;

        const scroller = getScroller?.() ?? document.querySelector('[data-section-container]');
        if (!scroller) return;

        scroller.addEventListener('scroll', stableOnDismiss);
        return () => scroller.removeEventListener('scroll', stableOnDismiss);
    }, [active, stableOnDismiss, getScroller]);
}
